// src/services/notificationService.js
import { supabase } from "../lib/supabase";
import { messageTemplateService } from "./messageTemplateService";

// Mantiene 1 canal realtime activo por vez
let _notificationsSubscription = null;

export function mapNotificationRow(row) {
  if (!row) return null;

  const createdAt = row.created_at || row.createdAt || null;

  return {
    id: row.id,
    userId: row.user_id || row.userId,
    type: row.type,
    title: row.title,
    message: row.message,
    link: row.link,
    read: row.read,
    priority: row.priority,
    metadata: row.metadata,
    createdAt,
  };
}

export const notificationService = {
  /**
   * ✅ IMPORTANTE:
   * - SIEMPRE filtra por user_id (aunque sea admin).
   * - Esto evita que un admin vea 4 filas (una por cada admin).
   */
  async getNotifications(userId, optionsOrLimit = 50) {
    try {
      const options =
        typeof optionsOrLimit === "number"
          ? { limit: optionsOrLimit }
          : { limit: Number(optionsOrLimit?.limit || 50) };

      if (!userId) return [];

      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(options.limit);

      if (error) throw error;

      return (data || []).map(mapNotificationRow).filter(Boolean);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      throw error;
    }
  },

  /**
   * ✅ Unread count SOLO del usuario.
   * Si luego quieres "contador global admin", eso es otro feature (notifs globales).
   */
  async getUnreadCount(userId) {
    try {
      if (!userId) return 0;

      const { count, error } = await supabase
        .from("notifications")
        .select("*", { count: "exact", head: true })
        .eq("user_id", userId)
        .eq("read", false);

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error("Error fetching unread count:", error);
      return 0;
    }
  },

  async markAsRead(notificationId) {
    try {
      if (!notificationId) return;
      const { error } = await supabase
        .from("notifications")
        .update({ read: true })
        .eq("id", notificationId);

      if (error) throw error;
    } catch (error) {
      console.error("Error marking notification as read:", error);
      throw error;
    }
  },

  /**
   * ✅ Marca como leído SOLO lo del usuario logueado.
   */
  async markAllAsRead(userId) {
    try {
      if (!userId) return;

      const { error } = await supabase
        .from("notifications")
        .update({ read: true })
        .eq("user_id", userId)
        .eq("read", false);

      if (error) throw error;
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      throw error;
    }
  },

  async deleteNotification(notificationId) {
    try {
      if (!notificationId) return;
      const { error } = await supabase
        .from("notifications")
        .delete()
        .eq("id", notificationId);

      if (error) throw error;
    } catch (error) {
      console.error("Error deleting notification:", error);
      throw error;
    }
  },

  /**
   * ✅ REALTIME
   * - 1 canal activo a la vez.
   * - SIEMPRE filtra por user_id del usuario logueado (admin incluido).
   * - callback recibe el payload raw de Supabase.
   */
  subscribeToNotifications(userId, callback) {
    if (!userId) return null;

    // Limpia canal anterior si existe
    if (_notificationsSubscription) {
      try {
        supabase.removeChannel(_notificationsSubscription);
      } catch (_) {}
      _notificationsSubscription = null;
    }

    const channelName = `notifications_changes_${userId}`;

    const config = {
      event: "*",
      schema: "public",
      table: "notifications",
      filter: `user_id=eq.${userId}`, // ✅ clave: solo las del usuario
    };

    _notificationsSubscription = supabase
      .channel(channelName)
      .on("postgres_changes", config, (payload) => {
        try {
          callback?.(payload);
        } catch (e) {
          console.error("Notifications realtime callback error:", e);
        }
      })
      .subscribe();

    return _notificationsSubscription;
  },

  /**
   * ✅ Desuscripción segura
   */
  unsubscribe() {
    if (_notificationsSubscription) {
      try {
        supabase.removeChannel(_notificationsSubscription);
      } catch (_) {}
      _notificationsSubscription = null;
    }
  },

  /**
   * ✅ Método utilitario si en algún lado guardas un channel específico.
   */
  unsubscribeFromNotifications(subscription) {
    try {
      if (subscription) supabase.removeChannel(subscription);
    } catch (_) {}
  },

  // Templates (wrappers)
  async getMessageTemplates(filters = {}) {
    try {
      const mappedFilters = {
        ...filters,
        ...(filters?.activeOnly !== undefined ? { isActive: !!filters.activeOnly } : {}),
      };
      if (filters?.isActive !== undefined) mappedFilters.isActive = !!filters.isActive;
      return await messageTemplateService.getTemplates(mappedFilters);
    } catch (error) {
      console.error("Error fetching message templates:", error);
      throw error;
    }
  },

  async createMessageTemplate(templateData, userId) {
    try {
      const payload = {
        ...templateData,
        createdBy: templateData?.createdBy || userId,
      };
      return await messageTemplateService.createTemplate(payload);
    } catch (error) {
      console.error("Error creating message template:", error);
      throw error;
    }
  },

  // Preferencias
  async getNotificationPreferences(userId) {
    try {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("notification_preferences")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("Error loading preferences:", error);
      throw error;
    }
  },

  async upsertNotificationPreferences(userId, prefs = []) {
    try {
      if (!userId) throw new Error("Missing userId");

      const rows = (prefs || []).map((p) => ({
        user_id: userId,
        channel: p.channel,
        notification_type: p.notification_type,
        enabled: !!p.enabled,
        schedule: p.schedule ?? null,
        updated_at: new Date().toISOString(),
      }));

      const { data, error } = await supabase
        .from("notification_preferences")
        .upsert(rows, { onConflict: "user_id,channel,notification_type" })
        .select("*");

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("Error saving preferences:", error);
      throw error;
    }
  },

  async setNotificationPreference(userId, pref) {
    try {
      if (!userId) throw new Error("Missing userId");
      if (!pref?.channel || !pref?.notification_type) throw new Error("Missing channel/notification_type");

      const row = {
        user_id: userId,
        channel: pref.channel,
        notification_type: pref.notification_type,
        enabled: !!pref.enabled,
        schedule: pref.schedule ?? null,
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from("notification_preferences")
        .upsert([row], { onConflict: "user_id,channel,notification_type" })
        .select("*")
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error setting preference:", error);
      throw error;
    }
  },
};

export default notificationService;
